import { Copy, Printer, Check, Minus, FileText, Timer, Award } from "lucide-react";
import { Card, PageHeader, Btn, BtnLink } from "@/components/kit";
import DataPanel from "@/components/DataPanel";
import { BRAND } from "@/config/brand";
import { ANGLES, STORYBOARD, PREP_CHECKLIST, MAKING_STEPS, EVAL_CRITERIA, EVAL_CHOICES } from "@/modules/premiere-video/content";
import { useProgress } from "@/modules/premiere-video/store";
import { assembleScript, filled, doneCount, stepsCount } from "@/modules/premiere-video/logic";
import { copyScript, ScriptStats } from "@/modules/premiere-video/missions/Mission4";
import { conceptSummary } from "@/modules/premiere-video/missions/Mission3";

const Empty = ({ m }) => <p className="text-sm italic text-soft">Non renseigné — à compléter dans la mission {m}.</p>;

function Row({ k, v }) {
  if (!filled(v)) return null;
  return <div className="grid gap-1 border-b border-line py-2.5 last:border-0 sm:grid-cols-[200px_1fr] sm:gap-4"><dt className="text-sm font-semibold text-bx-2">{k}</dt><dd className="whitespace-pre-line break-words text-ink">{v}</dd></div>;
}

function Section({ title, testId, children }) {
  return <Card data-testid={testId}><h2 className="mb-4 text-2xl font-medium text-bx">{title}</h2>{children}</Card>;
}

const Tick = ({ on, label }) => (
  <li className="flex items-center gap-2.5 py-1">
    <span className={`flex h-5 w-5 items-center justify-center rounded-md ${on ? "bg-bx text-white" : "border border-line text-soft"}`}>{on ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <Minus className="h-3 w-3" />}</span>
    <span className={on ? "text-ink" : "text-soft"}>{label}</span>
  </li>
);

export default function Results() {
  const { state } = useProgress();
  const { m2, m3, m5, m6, m7 } = state;
  const script = assembleScript(state.m4);
  const angle = ANGLES.find((a) => a.id === m3.angle);
  const hasProduct = filled(m2.name);
  const hasConcept = angle || filled(m3.idea);
  const evalLabel = (id) => EVAL_CHOICES.find((c) => c.id === id)?.label;

  return (
    <div data-testid="results-page">
      <div className="print-only mb-6 border-b border-line pb-4">
        <p className="font-sans text-lg font-bold tracking-[0.14em] text-bx">{BRAND.name}</p>
        <p className="text-sm text-soft">Ma première vidéo UGC — récapitulatif</p>
      </div>
      <PageHeader eyebrow="Récapitulatif" title="Mes résultats" subtitle={`Tout ce que tu as construit pendant le parcours. ${doneCount(state)}/7 missions terminées.`}>
        <div className="no-print mt-6 flex flex-wrap gap-3">
          <Btn onClick={() => copyScript(script)} data-testid="results-copy-script-button"><Copy className="h-4 w-4" />Copier mon script</Btn>
          <Btn variant="outline" onClick={() => window.print()} data-testid="print-recap-button"><Printer className="h-4 w-4" />Imprimer mon récapitulatif</Btn>
          <BtnLink to="/script/imprimer" variant="outline" data-testid="results-script-pdf-link"><FileText className="h-4 w-4" />Mon script en PDF</BtnLink>
          <BtnLink to="/script/repeter" variant="gold" data-testid="results-rehearse-link"><Timer className="h-4 w-4" />Répéter mon script</BtnLink>
          {doneCount(state) === 7 && <BtnLink to="/certificat" variant="outline" data-testid="results-certificate-link"><Award className="h-4 w-4" />Mon certificat</BtnLink>}
        </div>
      </PageHeader>
      <div className="space-y-5">
        <Section title="Mon produit" testId="results-product">
          {hasProduct ? <dl><Row k="Produit" v={m2.name} /><Row k="Catégorie" v={m2.category} /><Row k="Pourquoi je l'ai choisi" v={m2.why} /><Row k="Utilité principale" v={m2.utility} /><Row k="Détail à montrer" v={m2.detail} /></dl> : <Empty m="02" />}
        </Section>
        <Section title="Mon angle et mon concept" testId="results-concept">
          {hasConcept ? (
            <dl><Row k="Angle" v={angle?.label} /><Row k="Idée principale" v={m3.idea} /><Row k="Résumé" v={conceptSummary(m3, m2.name).join("\n")} /></dl>
          ) : <Empty m="03" />}
        </Section>
        <Section title="Mon script" testId="results-script">
          {script ? <><p className="whitespace-pre-line font-display text-lg leading-relaxed text-ink">{script}</p><div className="mt-4"><ScriptStats script={script} /></div></> : <Empty m="04" />}
        </Section>
        <Section title="Mon storyboard" testId="results-storyboard">
          <ol className="grid gap-3 sm:grid-cols-2">
            {STORYBOARD.map((p, i) => (
              <li key={p.id} className="rounded-2xl bg-ivory p-4">
                <p className="font-display text-bx">{p.title}</p>
                <p className="mt-1 text-sm text-ink/85">{filled(m5.notes[p.id]) ? m5.notes[p.id] : p.action}</p>
                <p className={`mt-2 text-xs font-semibold ${m5.filmed[p.id] ? "text-bx" : "text-soft"}`}>{m5.filmed[p.id] ? "Plan filmé" : "Pas encore filmé"}</p>
              </li>
            ))}
          </ol>
        </Section>
        <Section title="Mes checklists" testId="results-checklists">
          <div className="grid gap-6 sm:grid-cols-2">
            <div><p className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-soft">Préparation</p><ul>{PREP_CHECKLIST.map((it) => <Tick key={it.id} on={m5.checklist[it.id]} label={it.label} />)}</ul></div>
            <div>
              <p className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-soft">Réalisation · {stepsCount(state)}/9</p>
              <ul>{MAKING_STEPS.map((it) => <Tick key={it.id} on={m6.steps[it.id]} label={it.label} />)}</ul>
              <dl className="mt-3"><Row k="Outil de montage" v={m6.tool} /><Row k="Vidéo enregistrée" v={m6.location} /></dl>
            </div>
          </div>
        </Section>
        <Section title="Mon bilan final" testId="results-bilan">
          <ul className="mb-4 space-y-1.5">
            {EVAL_CRITERIA.map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 border-b border-line py-2 text-sm last:border-0">
                <span className="text-ink">{c.label}</span>
                <span className={`font-semibold ${m7.evals[c.id] === "yes" ? "text-bx" : m7.evals[c.id] === "improve" ? "text-[#8A6A35]" : "text-soft"}`}>{evalLabel(m7.evals[c.id]) || "Non évalué"}</span>
              </li>
            ))}
          </ul>
          {filled(m7.strengths) || filled(m7.improvements) || filled(m7.learned) ? (
            <dl><Row k="Mes points forts" v={m7.strengths} /><Row k="Mes améliorations" v={m7.improvements} /><Row k="Ce que j'ai appris" v={m7.learned} /></dl>
          ) : <Empty m="07" />}
        </Section>
        <DataPanel />
      </div>
    </div>
  );
}
